import React from 'react';
import { Box, List, ListItem, ListItemText, Collapse, Typography, Chip } from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EventItem from './EventItem';
import { convertToHumanReadableTime } from '../../utils/helpers';
import CameraHighlightService from '../../utils/cameraHighlightService';

// Priority level color mapping
const PRIORITY_COLORS = {
  'High': '#f44336',
  'Moderate': '#ff9800',
  'Low': '#4caf50'
};

// Updated functions for LocationGroup.js

// UPDATED: Separate detection checks for PD and ID
const LocationGroup = ({
  location,
  locationData,
  expanded,
  onToggle,
  onEventClick,
  getEventPriority,
  getCameraByIp,
  isReportView = false,
  liveVtsClosedCache = {},
  onLiveVtsEventClosed
}) => {
  const { events, hasSDLocation } = locationData;
  
  const latestEvent = events[0]; 
  
  const isHighSurveillance = events.some(event => 
    event.cameraIp && CameraHighlightService.isHighSurveillance(event.cameraIp)
  );
  
  const isKusundaArea = location.toLowerCase().includes("kusunda");
  const shouldHighlight = isHighSurveillance || isKusundaArea;

  // Vehicle alert group detection
  const isVehicleAlertGroup = events.some(event => event.isVehicleAlert || event.alertType);

  // UPDATED: Separate detection checks for PD and ID
  const hasPersonDetection = events.some(event => 
    event.detectionType === 'PD' || 
    event.type === 'PD' || 
    event.originalMessage?.eventType === 'PD'
  );

  const hasIntrusionDetection = events.some(event => 
    event.detectionType === 'ID' || 
    event.type === 'ID' || 
    event.originalMessage?.eventType === 'ID'
  );

  // Other event type detections remain the same...
  const hasLoadingStateEvents = events.some(event => 
    event.type === 'LS' || event.originalMessage?.eventType === 'LS'
  );

  // Count separate PD and ID events
  const pdCount = events.filter(event => 
    event.detectionType === 'PD' || 
    event.type === 'PD' || 
    event.originalMessage?.eventType === 'PD'
  ).length;

  const idCount = events.filter(event => 
    event.detectionType === 'ID' || 
    event.type === 'ID' || 
    event.originalMessage?.eventType === 'ID'
  ).length;

  const lsCount = events.filter(event => 
    event.type === 'LS' || event.originalMessage?.eventType === 'LS'
  ).length;

  // Vehicle alert counts
  const offRouteCount = events.filter(event => 
    event.alertType === 'off-route' || event.eventType === 'Off-Route Alert'
  ).length;

  // NEW: Off-area alert count
  const offAreaCount = events.filter(event => 
    event.isOffAreaAlert || event.alertType === 'off-area'
  ).length;

  const tamperCount = events.filter(event => 
    event.alertType === 'tamper' || event.eventType === 'Tamper Alert'
  ).length;

  // Priority counting logic remains the same...
  const getEventPriorityForCounting = (event) => {
    if (!isReportView && getEventPriority) {
      const livePriority = getEventPriority(event.id || event.eventId);
      if (livePriority) {
        return livePriority.severity;
      }
    }
    
    return event.severity;
  };

  const highPriorityCount = events.filter(event => getEventPriorityForCounting(event) === 'High').length;
  const moderatePriorityCount = events.filter(event => getEventPriorityForCounting(event) === 'Moderate').length;
  const lowPriorityCount = events.filter(event => getEventPriorityForCounting(event) === 'Low').length;
  const hasPriorityEvents = highPriorityCount > 0 || moderatePriorityCount > 0 || lowPriorityCount > 0;

  // UPDATED: Enhanced border color logic - off-area gets highest priority
  const getBorderColor = () => {
    if (highPriorityCount > 0) return "4px solid #f44336";
    if (moderatePriorityCount > 0) return "3px solid #ff9800";
    if (lowPriorityCount > 0) return "3px solid #4caf50";
    
    // NEW: Off-area gets highest priority after priorities
    if (offAreaCount > 0) return "4px solid #FF1744"; // Bright red for off-area
    
    if (hasIntrusionDetection) return "3px solid #FFC107"; // Yellow for ID only
    if (hasLoadingStateEvents) return "3px solid #FFA500"; 
    if (offRouteCount > 0) return "3px solid #FF6B35";
    if (tamperCount > 0) return "3px solid #DC143C";
    if (shouldHighlight) return "3px solid rgba(244,67,54,0.7)";
    if (hasSDLocation) return "3px solid rgba(244,67,54,0.5)";
    return "none";
  };

  // UPDATED: Enhanced background color logic - off-area priority
  const getBackgroundColor = () => {
    if (highPriorityCount > 0) return "rgba(244,67,54,0.15)";
    if (moderatePriorityCount > 0) return "rgba(255,152,0,0.1)";
    if (lowPriorityCount > 0) return "rgba(76,175,80,0.12)";
    
    // NEW: Off-area alert background
    if (offAreaCount > 0) return "rgba(255, 23, 68, 0.2)"; // Bright red background
    
    if (hasIntrusionDetection) return "rgba(255, 193, 7, 0.2)"; // Yellow for ID only
    if (hasLoadingStateEvents) return "rgba(255,165,0,0.15)"; 
    if (offRouteCount > 0) return "rgba(255,107,53,0.15)";
    if (tamperCount > 0) return "rgba(220,20,60,0.15)";
    if (shouldHighlight) return "rgba(244,67,54,0.08)";
    if (hasSDLocation) return "rgba(244,67,54,0.05)";
    return "rgba(255,255,255,0.05)";
  };

  // UPDATED: Enhanced hover color logic - off-area priority
  const getHoverColor = () => {
    if (highPriorityCount > 0) return 'rgba(244,67,54,0.2)';
    if (moderatePriorityCount > 0) return 'rgba(255,152,0,0.15)';
    if (lowPriorityCount > 0) return 'rgba(76,175,80,0.15)';
    
    // NEW: Off-area hover color
    if (offAreaCount > 0) return 'rgba(255, 23, 68, 0.3)';
    
    if (hasIntrusionDetection) return 'rgba(255, 193, 7, 0.3)'; // Yellow for ID only
    if (hasLoadingStateEvents) return 'rgba(255,165,0,0.2)'; 
    if (offRouteCount > 0) return 'rgba(255,107,53,0.2)';
    if (tamperCount > 0) return 'rgba(220,20,60,0.2)';
    return 'rgba(76,175,80,0.1)';
  };

  // UPDATED: Enhanced text color logic - off-area priority
  const getTextColor = () => {
    if (highPriorityCount > 0) return "#f44336";
    if (moderatePriorityCount > 0) return "#ff9800";
    if (lowPriorityCount > 0) return "#4caf50";
    
    // NEW: Off-area text color
    if (offAreaCount > 0) return "#FF1744";
    
    if (hasIntrusionDetection) return "#FFC107"; // Yellow for ID only
    if (shouldHighlight) return "#f44336";
    if (hasSDLocation) return "#f44336";
    if (offRouteCount > 0) return "#FF6B35";
    if (tamperCount > 0) return "#DC143C";
    
    return "white";
  };
  
  const eventsWithComments = events.filter(event => {
    if (!isReportView && getEventPriority) {
      const livePriority = getEventPriority(event.id || event.eventId);
      if (livePriority && livePriority.remarks && livePriority.remarks.length > 0) {
        return true;
      }
    }
    return event.remarks && event.remarks.length > 0;
  }).length;

  // Count how many VTS events in this group have been closed this session
  const vtsClosedCount = isVehicleAlertGroup
    ? events.filter(event => {
        const key = event.vehicleNumber && event.timeStamp
          ? `${event.vehicleNumber}_${event.timeStamp}`
          : null;
        return key && liveVtsClosedCache[key];
      }).length
    : 0;

  // Group title generation (unchanged)
  const getGroupTitle = () => {
    if (isVehicleAlertGroup) {
      const vehicleCount = new Set(events.map(event => event.vehicleNumber)).size;
      return `${location} (${events.length} alerts, ${vehicleCount} vehicles)`;
    } else {
      return `${location} (${events.length})`;
    }
  };

  // Timestamp generation for location groups
  const getLocationTimestamp = () => {
    if (isVehicleAlertGroup) {
      if (latestEvent && latestEvent.readableTime) {
        const timePart = latestEvent.readableTime.split(', ')[1];
        return timePart || latestEvent.readableTime;
      }
      if (latestEvent && latestEvent.timestamp) {
        try {
          const istDate = new Date(latestEvent.timestamp);
          return istDate.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          });
        } catch (error) {
          console.warn('Error converting vehicle alert timestamp:', error);
        }
      }
      return "Recent Alert";
    }
    
    // For offline camera events, use their timestamp
    if (latestEvent && latestEvent.isOfflineCamera && latestEvent.timestamp) {
      try {
        const date = new Date(latestEvent.timestamp);
        return date.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
      } catch (error) {
        console.warn('Error converting offline camera timestamp:', error);
        return "Recent";
      }
    }
    
    // For regular camera events, use convertToHumanReadableTime
    if (latestEvent && latestEvent.id) {
      try {
        return convertToHumanReadableTime(latestEvent.id.split("_")[0]);
      } catch (error) {
        console.warn('Error converting timestamp for location group:', error);
        return "Recent";
      }
    }
    
    return "N/A";
  };

  // Derive the accent color (matches getTextColor logic) for the
  // gradient + glow used in the premium row treatment.
  const rowAccent = getTextColor() === 'white' ? '#1ABC9C' : getTextColor();
  const baseRowBg = getBackgroundColor();

  return (
    <React.Fragment>
      {/* Group Header */}
      <ListItem
        sx={{
          background: `linear-gradient(90deg, ${baseRowBg} 0%, rgba(22,32,45,0.55) 100%)`,
          marginBottom: "6px",
          borderRadius: "8px",
          padding: "10px 12px",
          fontSize: "14px",
          cursor: "pointer",
          borderLeft: getBorderColor(),
          border: '1px solid rgba(255,255,255,0.04)',
          borderLeftWidth: undefined,
          position: 'relative',
          overflow: 'hidden',
          transition: 'transform 0.18s ease, background 0.18s ease, box-shadow 0.18s ease',
          '&:hover': {
            background: `linear-gradient(90deg, ${getHoverColor()} 0%, rgba(22,32,45,0.7) 100%)`,
            transform: 'translateX(2px)',
            boxShadow: `0 4px 14px ${rowAccent}33`,
          },
          boxShadow: highPriorityCount > 0 ? '0 3px 12px rgba(244,67,54,0.28), inset 0 1px 0 rgba(255,255,255,0.04)' :
                     moderatePriorityCount > 0 ? '0 3px 12px rgba(255,152,0,0.22), inset 0 1px 0 rgba(255,255,255,0.04)' :
                     lowPriorityCount > 0 ? '0 3px 12px rgba(76,175,80,0.18), inset 0 1px 0 rgba(255,255,255,0.04)' :
                     offAreaCount > 0 ? '0 3px 12px rgba(255,23,68,0.32), inset 0 1px 0 rgba(255,255,255,0.04)' :
                     hasIntrusionDetection ? '0 3px 12px rgba(255,193,7,0.22), inset 0 1px 0 rgba(255,255,255,0.04)' :
                     '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.03)',
          animation: 'slideIn 0.5s ease-out',
        }}
        onClick={onToggle}
      >
        <ListItemText
          primary={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: getTextColor(),
                pl: "0.5vw",
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.86rem',
                    letterSpacing: '0.01em',
                    textShadow: rowAccent !== '#1ABC9C' ? `0 0 10px ${rowAccent}40` : 'none',
                  }}
                >
                  {getGroupTitle()}
                </Typography>
                
                <Box sx={{ display: 'flex', ml: 1, gap: 0.5 }}>
                  {/* Vehicle Alert Type Indicators */}
                  
                  {/* NEW: Off-area alert chip - shown FIRST for highest priority */}
                  {offAreaCount > 0 && (
                    <Chip 
                      label={`${offAreaCount} OFF-AREA`} 
                      size="small" 
                      sx={{ 
                        height: 18, 
                        fontSize: '0.6rem', 
                        bgcolor: '#FF1744',
                        color: 'white',
                        fontWeight: 'bold',
                        animation: 'pulse 2s infinite' // Optional pulsing
                      }} 
                    />
                  )}
                  
                  {offRouteCount > 0 && (
                    <Chip 
                      label={`${offRouteCount} Off-Route`} 
                      size="small" 
                      sx={{ 
                        height: 18, 
                        fontSize: '0.6rem', 
                        bgcolor: '#FF6B35',
                        color: 'white' 
                      }} 
                    />
                  )}
                  
                  {tamperCount > 0 && (
                    <Chip 
                      label={`${tamperCount} Tamper`} 
                      size="small" 
                      sx={{ 
                        height: 18, 
                        fontSize: '0.6rem', 
                        bgcolor: '#DC143C',
                        color: 'white' 
                      }} 
                    />
                  )}
                  
                  {/* PD count without special styling */}
                  {pdCount > 0 && (
                    <Chip 
                      label={`${pdCount} PD`} 
                      size="small" 
                      sx={{ 
                        height: 18, 
                        fontSize: '0.6rem', 
                        bgcolor: 'rgba(255,255,255,0.2)', // Default styling like other events
                        color: 'white',
                        fontWeight: 'normal'
                      }} 
                    />
                  )}
                  
                  {/* Keep ID chip with yellow styling */}
                  {idCount > 0 && (
                    <Chip 
                      label={`${idCount} ID`} 
                      size="small" 
                      sx={{ 
                        height: 18, 
                        fontSize: '0.6rem', 
                        bgcolor: '#FFC107', // Yellow for ID
                        color: 'black',
                        fontWeight: 'bold'
                      }} 
                    />
                  )}
                  
                  {lsCount > 0 && (
                    <Chip 
                      label={`${lsCount} LS`} 
                      size="small" 
                      sx={{ 
                        height: 18, 
                        fontSize: '0.6rem', 
                        bgcolor: '#FFA500',
                        color: 'white' 
                      }} 
                    />
                  )}
                  
                  {/* VTS session-closed chip */}
                  {vtsClosedCount > 0 && (
                    <Chip
                      label={`${vtsClosedCount} Closed`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        bgcolor: 'rgba(39,174,96,0.25)',
                        color: '#27ae60',
                        border: '1px solid rgba(39,174,96,0.4)',
                        fontWeight: 'bold'
                      }}
                    />
                  )}

                  {/* Priority chips */}
                  {highPriorityCount > 0 && (
                    <Chip 
                      label={`${highPriorityCount} High`} 
                      size="small" 
                      sx={{ 
                        height: 18, 
                        fontSize: '0.6rem', 
                        bgcolor: PRIORITY_COLORS.High,
                        color: 'white' 
                      }} 
                    />
                  )}
                  {moderatePriorityCount > 0 && (
                    <Chip 
                      label={`${moderatePriorityCount} Mod`} 
                      size="small" 
                      sx={{ 
                        height: 18, 
                        fontSize: '0.6rem', 
                        bgcolor: PRIORITY_COLORS.Moderate,
                        color: 'white' 
                      }} 
                    />
                  )}
                  {lowPriorityCount > 0 && (
                    <Chip 
                      label={`${lowPriorityCount} Low`} 
                      size="small" 
                      sx={{ 
                        height: 18, 
                        fontSize: '0.6rem', 
                        bgcolor: PRIORITY_COLORS.Low,
                        color: 'white' 
                      }} 
                    />
                  )}
                  {eventsWithComments > 0 && (
                    <Chip 
                      label={`${eventsWithComments} ${eventsWithComments === 1 ? 'Comment' : 'Comments'}`} 
                      size="small" 
                      sx={{ 
                        height: 18, 
                        fontSize: '0.6rem', 
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white' 
                      }} 
                    />
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    mr: 1,
                    fontSize: '0.78rem',
                    fontWeight: 500,
                    opacity: 0.85,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {getLocationTimestamp()}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    transition: 'transform 0.2s ease, background 0.2s ease',
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
                  }}
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </Box>
              </Box>
            </Box>
          }
        />
      </ListItem>

      {/* Collapsible Events List */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {events.map((event, index) => (
            <EventItem
              key={event.id}
              event={{
                ...event,
                displayName: event.displayName || `Event ${index + 1} - ${location}`
              }}
              index={index}
              location={location}
              isHighSurveillance={event.cameraIp && CameraHighlightService.isHighSurveillance(event.cameraIp)}
              onEventClick={onEventClick}
              getEventPriority={getEventPriority}
              getCameraByIp={getCameraByIp}
              isReportView={isReportView}
              liveVtsClosedCache={liveVtsClosedCache}
              onLiveVtsEventClosed={onLiveVtsEventClosed}
            />
          ))}
        </List>
      </Collapse>
    </React.Fragment>
  );
};

export default LocationGroup;